import type { Topology } from "utils/math/geometry/findClosestOrthogonalProjection/types";
import { SCALE_FACTOR, VERTICAL_DROP_PER_CIRCUIT } from "./constants";
import type { Node, Edge } from "./types";

export const roundTo = (n: number, decimals: number): number => {
  const p = 10 ** decimals;
  return Math.round(n * p) / p;
};

export const pixelsToMeters = (
  pixelDistance: number,
  scaleFactor: number = SCALE_FACTOR,
): number => {
  return pixelDistance / scaleFactor;
};

export const metersToPixels = (
  meterDistance: number,
  scaleFactor: number = SCALE_FACTOR,
): number => {
  return meterDistance * scaleFactor;
};

// Calculate total cable length in meters
export const calculateTotalCableLength = (
  total: number,
  verticalDrop: number = VERTICAL_DROP_PER_CIRCUIT,
): number => {
  const trayLengthMeters = pixelsToMeters(total);
  return trayLengthMeters + verticalDrop;
};

export const formatDistance = (
  pixelDistance: number,
  unit: "px" | "m" = "m",
  decimals: number = 1,
): string => {
  if (unit === "px") {
    return `${pixelDistance.toFixed(decimals)} px`;
  }
  const meters = pixelsToMeters(pixelDistance);
  return `${meters.toFixed(decimals)} m`;
};

/**
 * Constructs a Topology (Edge List structure) from raw arrays of nodes and edges.
 * Converts IDs to strings and coordinates/weights to numbers.
 */
export const buildEdgeListTopology = (
  nodes: Node[],
  edges: Edge[],
): Topology => {
  return {
    nodes: Object.fromEntries(
      nodes.map((n) => [String(n.id), { x: Number(n.x), y: Number(n.y) }]),
    ),
    edges: edges.map((e) => ({
      from: String(e.fromNodeId),
      to: String(e.toNodeId),
      weight: Number(e.weight),
    })),
  };
};
